import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { useApiError } from '@/modules/common/hooks/use-api-error';

import { useSummary } from '../../hooks/use-summary';
import { useViewModeAndDate } from '../../hooks/use-view-mode-and-date';
import { ProjectCard } from '../project-card';
import { DayTimeOffCard } from './day-time-off-card';

/**
 * Day-view body: a card listing each assigned project with its tracked/booked
 * progress bar + timer controls. Mirrors WeekView/MonthView in shape so the
 * page only has to switch between the three at the top level.
 */
export function DayView() {
  const { t } = useTranslation('authenticated.timeTracking');
  const { getErrorMessage } = useApiError();
  const { selectedDate, viewMode } = useViewModeAndDate();

  const {
    data: summary,
    isLoading,
    isRefetching,
    error,
    refetch,
  } = useSummary({
    date: selectedDate,
    viewMode,
  });

  const projectTimeTrackingSummaries = summary?.projectSummaries;
  const timeOffEntries = summary?.timeOffSummary ?? [];
  const timeOffRequests = summary?.timeOffRequests ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="flex flex-col gap-4 p-6">
          <h2 className="text-lg font-semibold">{t('assignedProjects.title')}</h2>

          {error && !isLoading && (
            <div className="flex flex-col items-center gap-2 border-t py-4">
              <ButtonLoadable
                variant="outline"
                size="sm"
                showRefreshIcon
                isLoading={isRefetching}
                onClick={() => refetch()}
              >
                {t('assignedProjects.reload')}
              </ButtonLoadable>
              <p className="text-xs text-destructive">{getErrorMessage(error)}</p>
            </div>
          )}

          {isLoading && !error && (
            <div className="flex items-center justify-center border-t py-8">
              <Button variant="ghost" size="default" disabled className="opacity-50">
                <Loader2 className="h-4 w-4 animate-spin" />
              </Button>
            </div>
          )}

          {!isLoading &&
            !error &&
            projectTimeTrackingSummaries &&
            projectTimeTrackingSummaries.length > 0 && (
              <div className="flex flex-col gap-4">
                {projectTimeTrackingSummaries.map(item => (
                  <ProjectCard key={item.project.projectId} project={item} />
                ))}
              </div>
            )}

          {!isLoading &&
            !error &&
            projectTimeTrackingSummaries &&
            projectTimeTrackingSummaries.length === 0 && (
              <div className="border-t py-8 text-center">
                <p className="text-sm text-muted-foreground">{t('emptyState.noProjects')}</p>
              </div>
            )}
        </div>
      </div>

      {!isLoading && !error && timeOffEntries.length > 0 && (
        <DayTimeOffCard
          entries={timeOffEntries}
          requests={timeOffRequests}
          totalCapacityHours={summary?.totalCapacityHours ?? 0}
          date={selectedDate}
        />
      )}
    </div>
  );
}
